import { useQuery } from '@tanstack/react-query';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';

import { passwordStatusOptions } from '@/entities/user';
import { SettingsRow } from '@/shared/ui/settings/settings-row';

import { useEmailChange } from '../../model/use-email-change';
import { ProfileEmailChangeDialog } from './profile-email-row/profile-email-change-dialog';
import { ProfileEmailVerificationBadge } from './profile-email-row/profile-email-verification-badge';

type Props = {
    email: string;
    isVerified: boolean | undefined;
    isResending: boolean;
    onResend: () => void;
    isDisabled?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
};

export function ProfileEmailRow({
    email,
    isVerified,
    isResending,
    onResend,
    isDisabled,
    onEditingChange,
}: Props) {
    const { data: passwordStatus } = useQuery(passwordStatusOptions());
    const {
        isOpen,
        step,
        isConfirmingIdentity,
        isSubmitting,
        identityError,
        addressError,
        startEmailChange,
        closeEmailChange,
        confirmIdentity,
        submitNewEmail,
    } = useEmailChange();

    const canEdit = Boolean(passwordStatus?.hasPassword);

    const handleStartEmailChange = () => {
        startEmailChange();
        onEditingChange?.(true);
    };

    const handleCloseEmailChange = () => {
        closeEmailChange();
        onEditingChange?.(false);
    };

    return (
        <>
            <SettingsRow
                label="Email"
                caption={email}
                badge={
                    isVerified !== undefined && (
                        <ProfileEmailVerificationBadge
                            variant={isVerified ? 'verified' : 'unverified'}
                        />
                    )
                }
                action={
                    canEdit
                        ? {
                              label: 'Edit',
                              onClick: handleStartEmailChange,
                              disabled: isDisabled,
                              ariaLabel: 'Edit email',
                          }
                        : undefined
                }
                className={cn(isDisabled && 'opacity-50')}
            >
                {isVerified === false && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="self-start"
                        onClick={onResend}
                        disabled={isResending}
                    >
                        {isResending ? 'Sending…' : 'Resend Verification Link'}
                    </Button>
                )}
            </SettingsRow>

            <ProfileEmailChangeDialog
                open={isOpen}
                step={step}
                isConfirmingIdentity={isConfirmingIdentity}
                isSubmitting={isSubmitting}
                identityError={identityError}
                addressError={addressError}
                onConfirmIdentity={confirmIdentity}
                onSubmitNewEmail={submitNewEmail}
                onClose={handleCloseEmailChange}
            />
        </>
    );
}
